// State invariants (IMPLEMENTATION §6.1 "Invariants"): conservation per material, unique ids, cell ↔ entity
// consistency, cash ≥ 0, income buckets ≥ 0, valid and discovered tiers in every container (floor, building
// contents via ledger.forEachStoredRef, Crate counts), id-ordered arrays, integer-only state. Used by the fuzz
// tests, the balance sim (M4b) and debug builds. Returns human-readable violations (empty = healthy).

import { cycleCost, maxLevelOf, recipeOf, slotCap, slotFams } from './bdefs.ts';
import { cellIndex } from './core/grid.ts';
import { canonicalJson } from './core/hash.ts';
import type { Int } from './core/types.ts';
import { isActive, type SimDefs } from './defs.ts';
import { famDef, isValidItem } from './entities.ts';
import type { FamId } from './gen/ids.ts';
import { ghostReqs } from './ghosts.ts';
import { conservationErrors, forEachStoredRef } from './ledger.ts';
import { isOpen, maskOf } from './occupancy.ts';
import { ringSlots } from './readout.ts';
import type { Building, ItemRef, SimState } from './state.ts';

export interface InvariantOptions {
  /** The state was just produced by tick() (no commands since): also checks the R1 carry cap. */
  afterTick?: boolean;
}

/** Per-building shape and capacity rules (levels, slot families and caps, buffers, heat, ghosts). */
export function buildingErrors(d: SimDefs, b: Building): string[] {
  const out: string[] = [];
  const at = `${b.type} ${b.id}`;
  if (!Number.isInteger(b.level) || b.level < 1 || b.level > maxLevelOf(d, b)) {
    out.push(`${at}: level ${b.level} out of range`);
  }
  if (b.stall !== null && !d.stalls.some((x) => x.id === b.stall)) out.push(`${at}: unknown stall`);
  if (b.ghost !== null) {
    const reqs = ghostReqs(d, b.type);
    if (reqs.length === 0) out.push(`${at}: a ghost of a type without requirements`);
    if (b.ghost.filled.length !== reqs.length)
      out.push(`${at}: ghost filled/requirements mismatch`);
    let done = reqs.length > 0;
    reqs.forEach((r, i) => {
      const f = b.ghost?.filled[i] ?? 0;
      if (f < 0 || f > r.n) out.push(`${at}: ghost requirement ${i} filled ${f}/${r.n}`);
      if (f < r.n) done = false;
    });
    if (done) out.push(`${at}: a complete ghost was not built`);
    if (b.ghost.change < 0) out.push(`${at}: negative ghost change`);
  }
  const ring = ringSlots(d);
  switch (b.type) {
    case 'crate': {
      if (b.counts.length !== d.tierUnits.length)
        out.push(`${at}: counts length ${b.counts.length}`);
      let n = 0;
      for (const k of b.counts) n += k;
      if (n > (d.buildings.crate.capacity[b.level - 1] ?? 0))
        out.push(`${at}: over capacity (${n})`);
      if (b.fam !== null && famDef(d, b.fam).kind === 'gun') out.push(`${at}: holds guns`);
      break;
    }
    case 'chute':
      if (b.prog < 0 || b.prog > d.buildings.chute.hopTicks) out.push(`${at}: prog ${b.prog}`);
      if (b.carry === null && b.prog !== 0) out.push(`${at}: prog without an item`);
      break;
    case 'packer': {
      if (b.buf.length > d.buildings.packer.buffer) out.push(`${at}: buffer over capacity`);
      if (b.out.length > d.machineRules.outCap) out.push(`${at}: out over capacity`);
      for (const r of [...b.buf, ...b.out]) {
        if (b.lockFam === null || r.fam !== b.lockFam) out.push(`${at}: ${r.fam} outside the lock`);
      }
      if (b.busy.length !== ring) out.push(`${at}: busy ring length`);
      if (b.prog < 0) out.push(`${at}: negative prog`);
      break;
    }
    case 'gallery':
      if (b.queue.length > d.buildings.gallery.ammoQueue) out.push(`${at}: queue over capacity`);
      for (const r of b.queue) if (r.fam !== 'ammo') out.push(`${at}: ${r.fam} in the ammo queue`);
      if (b.cd < 0 || b.practice < 0) out.push(`${at}: negative counter`);
      break;
    case 'deposit':
    case 'drill':
    case 'dock':
      break;
    default: {
      const fams = slotFams(d, b);
      if (b.slots.length !== fams.length) out.push(`${at}: ${b.slots.length} slots`);
      const cap = slotCap(d, b.type);
      b.slots.forEach((slot, i) => {
        if (slot.length > cap) out.push(`${at}: slot ${i} over capacity`);
        for (const r of slot) if (r.fam !== fams[i]) out.push(`${at}: ${r.fam} in slot ${i}`);
      });
      if (b.out.length > d.machineRules.outCap) out.push(`${at}: out over capacity`);
      const heatCap = b.type === 'smelter' ? (d.buildings.smelter.heatCap[b.level - 1] ?? 0) : 0;
      if (b.heat < 0 || b.heat > heatCap) out.push(`${at}: heat ${b.heat} outside 0..${heatCap}`);
      const cost = cycleCost(d, b.type);
      if (b.proc === null) {
        if (b.prog !== 0) out.push(`${at}: idle with banked work ${b.prog}`);
      } else {
        if (!isValidItem(d, b.proc.fam, b.proc.tier)) out.push(`${at}: invalid proc`);
        if (b.proc.fam !== recipeOf(d, b).out)
          out.push(`${at}: proc ${b.proc.fam} is not its output`);
        if (b.prog < 0 || b.prog > cost) out.push(`${at}: prog ${b.prog} outside 0..${cost}`);
      }
      if (b.busy.length !== ring) out.push(`${at}: busy ring length`);
      break;
    }
  }
  return out;
}

/**
 * Progression bookkeeping (M4a): unlocks are known, unique, active and nested (every unlock's `after`
 * prerequisite is unlocked too); the goal points at an unlocked unlock; indexes, counters and the revealed
 * shop list are in range.
 */
function progressErrors(s: SimState, d: SimDefs): string[] {
  const out: string[] = [];
  const p = s.progress;
  p.unlocked.forEach((id, i) => {
    const u = d.unlocks.find((x) => x.id === id);
    if (u === undefined) out.push(`unknown unlock ${id}`);
    else {
      if (!isActive(d, u.version)) out.push(`inactive unlock ${id}`);
      if (u.after !== null && !p.unlocked.includes(u.after))
        out.push(`${id} unlocked without its prerequisite ${u.after}`);
    }
    if (p.unlocked.indexOf(id) !== i) out.push(`${id} unlocked twice`);
  });
  const goal = d.unlocks[p.goal];
  if (p.goal !== -1 && (goal === undefined || !p.unlocked.includes(goal.id)))
    out.push(`goal ${p.goal} is not an unlocked unlock`);
  if (p.tutorial < 0 || p.tutorial > d.tutorial.length) out.push(`tutorial ${p.tutorial}`);
  for (const k of [
    'merges',
    'sales',
    'holdHits',
    'handMerges',
    'firedBest',
    'firedAutoBest',
  ] as const)
    if (p[k] < 0) out.push(`progress.${k} negative`);
  if (p.firedAutoBest > p.firedBest) out.push('firedAutoBest above firedBest');
  if (p.bindingT.starved < 0 || p.bindingT.gunLimited < 0) out.push('negative binding streak');
  p.seen.forEach((id, i) => {
    if (!d.shop.some((x) => x.id === id)) out.push(`seen: unknown shop item ${id}`);
    if (p.seen.indexOf(id) !== i) out.push(`seen: ${id} twice`);
  });
  return out;
}

export function checkInvariants(s: SimState, d: SimDefs, opts: InvariantOptions = {}): string[] {
  const out: string[] = [];
  try {
    canonicalJson(s);
  } catch (err) {
    out.push(String(err));
  }
  out.push(...conservationErrors(s, d));
  if (s.wallet.cash < 0) out.push(`cash is negative (${s.wallet.cash})`);
  // A sale undo reverses income only inside the sale's own bucket (sales.ts), so no bucket goes negative.
  const inc = s.income;
  if (inc.accT < 0 || inc.accA < 0)
    out.push(`income accumulator negative (${inc.accT}/${inc.accA})`);
  for (let i = 0; i < inc.total.length; i++) {
    if ((inc.total[i] ?? 0) < 0 || (inc.auto[i] ?? 0) < 0) out.push(`income bucket ${i} negative`);
  }
  if (inc.autoFrom !== -1 && (inc.autoFrom < 1 || inc.autoFrom > s.tick))
    out.push(`income.autoFrom ${inc.autoFrom} is not a past tick (tick ${s.tick})`);
  if (inc.autoFrom === -1 && s.wallet.lifetimeAuto > 0)
    out.push('automated income was earned but income.autoFrom is unset');

  const ids: Int[] = [];
  const note = (id: Int, where: string): void => {
    if (id <= 0 || id >= s.nextId) out.push(`${where}: id ${id} outside 1..${s.nextId - 1}`);
    ids.push(id);
  };
  // Every item kind that exists must be valid and already discovered (M2 decision 12: discoveries are
  // recorded where items are created, so a creation path that forgets `discover` fails here).
  const discovered = s.progress.discovered;
  const noteKind = (fam: FamId, tier: Int, where: string): void => {
    if (!isValidItem(d, fam, tier)) out.push(`${where}: invalid item ${fam} T${tier}`);
    else if (!discovered.includes(`${fam}:${tier}`))
      out.push(`${where}: ${fam}:${tier} not discovered`);
  };
  const noteRef = (r: ItemRef, where: string): void => {
    note(r.id, where);
    noteKind(r.fam, r.tier, where);
  };

  const mask = maskOf(s, d);
  const occ = { cols: d.board.cols, rows: d.board.rows, mask, ids: [] as Int[] };
  const claim = (x: Int, y: Int, id: Int, where: string): void => {
    if (!isOpen(occ, x, y)) {
      out.push(`${where}: (${x},${y}) is outside the mask`);
      return;
    }
    const ci = cellIndex(occ.cols, x, y);
    const prev = occ.ids[ci];
    if (prev !== undefined) out.push(`${where}: cell (${x},${y}) also holds ${prev}`);
    occ.ids[ci] = id;
  };

  let last = 0;
  for (const it of s.floor) {
    if (it.id <= last) out.push(`floor not in ascending id order at ${it.id}`);
    last = it.id;
    noteRef(it, `floor ${it.id}`);
    claim(it.x, it.y, it.id, `floor ${it.id}`);
    if (it.tier > famDef(d, it.fam).maxTier) out.push(`floor ${it.id}: tier above max`);
  }
  last = 0;
  for (const b of s.buildings) {
    if (b.id <= last) out.push(`buildings not in ascending id order at ${b.id}`);
    last = b.id;
    note(b.id, `building ${b.id}`);
    claim(b.x, b.y, b.id, `building ${b.id}`);
    forEachStoredRef(b, (r, where) => noteRef(r, `${b.type} ${b.id} ${where}`));
    if (b.type === 'crate') {
      for (let t = 0; t < b.counts.length; t++) {
        const n = b.counts[t] ?? 0;
        if (n < 0) out.push(`crate ${b.id}: negative count at T${t + 1}`);
        if (n === 0) continue;
        if (b.fam === null) out.push(`crate ${b.id}: counts without a family`);
        else noteKind(b.fam, t + 1, `crate ${b.id} T${t + 1}`);
      }
    }
    out.push(...buildingErrors(d, b));
    if (b.type === 'deposit') {
      // The carry cap applies to what is left after a tick's spawns; hits dispatched since may exceed it.
      const hard = d.deposits.find((x) => x.id === b.ore)?.hardMilli ?? 0;
      const cap = d.depositRules.accCapHardness * hard;
      if (b.acc < 0 || (opts.afterTick === true && b.acc > cap)) {
        out.push(`deposit ${b.id}: acc ${b.acc} outside 0..${cap}`);
      }
    }
  }
  const sale = s.transient.lastSale;
  if (sale !== null) {
    note(sale.token, 'lastSale token');
    for (const it of sale.items) {
      if (it.id <= 0 || it.id >= s.nextId) out.push(`lastSale item id ${it.id} out of range`);
    }
  }
  const seen: boolean[] = [];
  for (const id of ids) {
    if (seen[id] === true) out.push(`duplicate id ${id}`);
    seen[id] = true;
  }
  const held = s.transient.held;
  if (held !== null && seen[held] !== true) out.push(`held ${held} does not exist`);
  if (s.transient.undo.length > d.economy.buildUndoDepth) out.push('build undo stack too deep');
  const m = d.economy.manual;
  const cdMax = m.sharedCooldown ? m.holdRepeatTicks : 0;
  if (s.transient.tapCd < 0 || s.transient.tapCd > cdMax) {
    out.push(`tap cooldown ${s.transient.tapCd} outside 0..${cdMax}`);
  }
  if (s.transient.tapBuf.length > (m.sharedCooldown ? m.bufferedTaps : 0))
    out.push('too many buffered taps');
  if (s.transient.hold !== null && s.transient.hold.t > m.holdRepeatTicks)
    out.push('hold counter overran');
  out.push(...progressErrors(s, d));
  for (const t of s.progress.firstBuilt) {
    if (s.progress.firstBuilt.indexOf(t) !== s.progress.firstBuilt.lastIndexOf(t)) {
      out.push(`firstBuilt lists ${t} twice`);
    }
  }
  return out;
}
